import type { Location, Turbine } from "../../generated-axios-client";
import type { IntlShape } from "@formatjs/intl";
import type { FormInstance } from "antd/es/form";
import { Form, Select, type SelectProps } from "antd";
import { useEffect, useMemo } from "react";
import { useWatch } from "antd/es/form/Form";

const locationSelectOptions = (locationItems: Array<Location>) => {
  const locationOptions = locationItems.map((item) => ({
    key: item.id,
    value: item.id,
    label: item.name,
  }));

  return locationOptions;
};

type LocationSelectFormItemProps = {
  intl: IntlShape;
  locationItems: Location[];
  turbineItems?: Turbine[];
  selectProps?: SelectProps<string>;
  form: FormInstance;
};

export const LocationSelectorFormItem = (props: LocationSelectFormItemProps) => {
  const { intl, locationItems, turbineItems, selectProps, form } = props;

  const options = useMemo(() => locationSelectOptions(locationItems), [locationItems]);

  const selectedTurbineId = useWatch<string | undefined>(["turbine"], form);

  const selectedTurbine = useMemo(
    () =>
      selectedTurbineId
        ? turbineItems?.find((item) => item.id === selectedTurbineId)
        : undefined,
    [selectedTurbineId, turbineItems]
  );

  const siteOfSelectedTurbine = selectedTurbine?.location ?? undefined;
  const selectedTurbineNotResolvableYet = !!selectedTurbineId && !selectedTurbine;

  // The site is always derived from the selected turbine, never picked by hand: this
  // re-asserts it on load and on every change of the turbine selection or of the
  // turbine list. This effect is the only writer of the field, which is why there is
  // no longer a clearing effect for sites missing from the options — a turbine change
  // sets the new site before the refetched options arrive, so option membership would
  // wipe the derived value. While the selected turbine is missing from turbineItems
  // (list still loading) the current value is left untouched instead of being cleared.
  useEffect(() => {
    if (selectedTurbineNotResolvableYet) {
      return;
    }
    if (form.getFieldValue("location") !== siteOfSelectedTurbine) {
      form.setFieldValue("location", siteOfSelectedTurbine);
    }
  }, [siteOfSelectedTurbine, selectedTurbineNotResolvableYet, form]);

  return (
    <Form.Item
      name="location"
      label={intl.formatMessage({ id: "ticket_location" })}
      rules={[
        {
          required: true,
          message: intl.formatMessage({ id: "validation_message_ticket_location" }),
        },
      ]}
    >
      <Select
        disabled
        placeholder={intl.formatMessage({ id: "ticket_location_placeholder" })}
        options={options}
        {...selectProps}
      />
    </Form.Item>
  );
};
